/**
 * BringFidoPreview — partnership pitch paste-up page.
 *
 * Purpose:
 *   A polished mockup demonstrating to BringFido (and to AADC members
 *   curious about the upcoming travel feature) exactly how their
 *   services would be presented inside AADC.
 *
 * NOT a live booking surface — every "Book" button on this page goes
 * to a placeholder route. Once the BringFido affiliate ID lands in
 * the env, this page becomes the template for the production /travel
 * surface.
 *
 * Designed to be share-able as a public URL — no auth required —
 * which makes it useful as an attachment in the partnership pitch
 * email.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Hotel, Plane, Car, MapPin, Star, Heart, Coins, ShieldCheck,
  ArrowRight, Dog, PawPrint, Calendar,
} from 'lucide-react';

const BRINGFIDO_URL = 'https://www.bringfido.com';

// Mocked sample listings — real data will come from BringFido's API
// or tracked deep-links once the partnership is live.
const SAMPLE_HOTELS = [
  {
    id: 'kimpton-ny',
    name: 'Kimpton Hotel Eventi',
    city: 'New York, NY',
    rating: 4.7,
    pet_fee: 0,
    weight_limit: null,
    dogs_allowed: 2,
    perks: ['Welcome biscuit', 'Dog bed on request', 'Yappy hour Tue/Thu'],
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
  },
  {
    id: 'aloft-austin',
    name: 'Aloft Austin Downtown',
    city: 'Austin, TX',
    rating: 4.5,
    pet_fee: 0,
    weight_limit: 40,
    dogs_allowed: 1,
    perks: ['ARF® program', 'Dog bowl & toy', 'Walking route maps'],
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
  },
  {
    id: 'laquinta-portland',
    name: 'La Quinta Inn Portland',
    city: 'Portland, OR',
    rating: 4.2,
    pet_fee: 0,
    weight_limit: 75,
    dogs_allowed: 2,
    perks: ['No pet fee', 'Pet-friendly floors', 'Near Forest Park trails'],
    image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80',
  },
];

const COMING_SOON = [
  { icon: Plane, label: 'Flights', sub: 'Pet-policy filter built in — see fees, breed restrictions, and in-cabin size limits before you book.' },
  { icon: Car, label: 'Car Rentals', sub: 'Cars that accept pets without surprise fees. AADC verifies fee transparency before listing.' },
  { icon: PawPrint, label: 'Vacation Rentals', sub: 'Dog-friendly Airbnb-style stays via BringFido\u2019s curated listings.' },
];

const Pill = ({ children, color = 'amber' }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
      color === 'green'
        ? 'bg-green-100 text-green-800'
        : color === 'blue'
          ? 'bg-blue-100 text-blue-800'
          : 'bg-amber-100 text-amber-800'
    }`}
  >
    {children}
  </span>
);

export default function BringFidoPreview() {
  // Click handler logs to our travel-credits attribution endpoint and
  // opens the partner URL in a new tab. Once approved, the tracking
  // URL will include the real affiliate ID; right now it just opens
  // the public BringFido page.
  const handleBookClick = (hotel) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/travel-credits/click`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        partner: 'bringfido',
        target_url: `${BRINGFIDO_URL}/hotel/${hotel.id}`,
        context: 'bringfido-preview',
      }),
    }).catch(() => {}); // best-effort — never block the booking
    window.open(`${BRINGFIDO_URL}/hotel/${hotel.id}`, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/40 to-background">
      {/* ====== HERO ====== */}
      <section className="container mx-auto px-4 pt-12 pb-10">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
              <Heart className="w-3 h-3 mr-1" />
              AADC × BringFido
            </Badge>
            <Pill color="blue">Preview · partnership pitch</Pill>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight leading-[1.05]">
            Travel with your dog,<br />
            <span className="text-amber-700">without the spreadsheet.</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-2xl">
            Every hotel, every flight, every rental, vetted for dog-friendliness — powered by{' '}
            <a href={BRINGFIDO_URL} className="font-medium text-amber-700 hover:underline" target="_blank" rel="noopener noreferrer">
              BringFido
            </a>
            , the most trusted name in pet travel. AADC members earn membership credit on every booking.
          </p>
          <div className="flex items-center gap-3 mt-7">
            <a
              href={BRINGFIDO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex"
            >
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
                Search hotels on BringFido
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground">
              Why we picked BringFido →
            </Link>
          </div>
        </div>
      </section>

      {/* ====== TRUST STRIP ====== */}
      <section className="border-y border-amber-200/50 bg-amber-50/60 py-6">
        <div className="container mx-auto px-4 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-2xl font-heading font-medium text-amber-700">25,000+</div>
            <div className="text-xs text-muted-foreground mt-1">Pet-friendly hotels</div>
          </div>
          <div>
            <div className="text-2xl font-heading font-medium text-amber-700">21 years</div>
            <div className="text-xs text-muted-foreground mt-1">BringFido in pet travel</div>
          </div>
          <div>
            <div className="text-2xl font-heading font-medium text-amber-700">$6.95</div>
            <div className="text-xs text-muted-foreground mt-1">Per AADC month earned per ~1 booking</div>
          </div>
          <div>
            <div className="text-2xl font-heading font-medium text-amber-700">$0</div>
            <div className="text-xs text-muted-foreground mt-1">Markup — book at BringFido's price</div>
          </div>
        </div>
      </section>

      {/* ====== SAMPLE HOTELS ====== */}
      <section className="container mx-auto px-4 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl font-heading font-medium flex items-center gap-2">
              <Hotel className="w-6 h-6 text-amber-700" />
              Featured pet-friendly hotels
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              A sample of what AADC members will see — full inventory on BringFido.
            </p>
          </div>
          <a
            href={BRINGFIDO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-amber-700 hover:underline whitespace-nowrap"
          >
            See all 25,000+ →
          </a>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SAMPLE_HOTELS.map((h) => (
            <Card key={h.id} className="overflow-hidden card-interactive">
              <div className="relative">
                <img
                  src={h.image}
                  alt={h.name}
                  className="w-full h-44 object-cover"
                  loading="lazy"
                />
                {h.pet_fee === 0 && (
                  <div className="absolute top-2 left-2">
                    <Pill color="green">
                      <Heart className="w-3 h-3" />
                      No pet fee
                    </Pill>
                  </div>
                )}
              </div>
              <CardContent className="p-4 space-y-3">
                <div>
                  <h3 className="font-heading font-medium leading-tight">{h.name}</h3>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {h.city}
                    <Star className="w-3 h-3 ml-2 text-amber-500 fill-amber-500" />
                    {h.rating}
                  </p>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Pill>
                    <Dog className="w-3 h-3" />
                    {h.dogs_allowed} dog{h.dogs_allowed > 1 ? 's' : ''} ok
                  </Pill>
                  {h.weight_limit && (
                    <Pill color="blue">≤ {h.weight_limit} lb</Pill>
                  )}
                  {!h.weight_limit && <Pill color="green">No weight limit</Pill>}
                </div>

                <ul className="text-xs text-muted-foreground space-y-1">
                  {h.perks.map((p) => (
                    <li key={p} className="flex items-start gap-1.5">
                      <PawPrint className="w-3 h-3 mt-0.5 text-amber-600 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  size="sm"
                  onClick={() => handleBookClick(h)}
                  data-testid={`bf-book-${h.id}`}
                >
                  Book on BringFido
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
                <p className="text-[10px] text-center text-muted-foreground">
                  Earns ~$5 toward your next AADC month
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ====== MEMBER PERK CALLOUT ====== */}
      <section className="container mx-auto px-4 pb-12">
        <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100/50">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex w-12 h-12 rounded-full bg-amber-600 items-center justify-center flex-shrink-0">
                <Coins className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-heading font-medium mb-2">
                  AADC pays for itself when you travel
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground mb-4">
                  Every BringFido booking through AADC earns you a credit toward your AADC membership renewal.
                  Roughly <strong>2 hotel stays</strong> = a free month. Roughly <strong>12 stays</strong> in a year =
                  a fully comped annual membership at our $60/yr rate.
                </p>
                <div className="grid sm:grid-cols-3 gap-4 mt-5">
                  <div className="rounded-lg bg-white p-4 border border-amber-200">
                    <div className="text-2xl font-heading font-medium text-amber-700">1 stay</div>
                    <div className="text-xs text-muted-foreground mt-1">~$5 toward AADC</div>
                  </div>
                  <div className="rounded-lg bg-white p-4 border border-amber-200">
                    <div className="text-2xl font-heading font-medium text-amber-700">2 stays</div>
                    <div className="text-xs text-muted-foreground mt-1">≈ 1 free month</div>
                  </div>
                  <div className="rounded-lg bg-white p-4 border border-amber-200">
                    <div className="text-2xl font-heading font-medium text-amber-700">12 stays</div>
                    <div className="text-xs text-muted-foreground mt-1">≈ Free annual membership</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ====== COMING SOON ====== */}
      <section className="container mx-auto px-4 pb-14">
        <h2 className="text-2xl font-heading font-medium mb-2 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-amber-700" />
          Also coming via BringFido
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          The same membership-credit system applies across all BringFido bookings.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          {COMING_SOON.map(({ icon: Icon, label, sub }) => (
            <Card key={label} className="border-dashed">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-5 h-5 text-amber-700" />
                  <h3 className="font-heading font-medium">{label}</h3>
                  <Pill color="blue">Soon</Pill>
                </div>
                <p className="text-sm text-muted-foreground">{sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ====== PARTNERSHIP NOTE (only on the preview build) ====== */}
      <section className="container mx-auto px-4 pb-16">
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-5 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">
                Note to BringFido team — this is a partnership preview
              </p>
              <p className="text-blue-800">
                Live build will swap our placeholder links for tracked affiliate URLs the moment our
                application is approved. Click tracking is already running (see{' '}
                <span className="font-mono text-xs">POST /api/travel-credits/click</span>) so we can attribute
                commissions to specific members on day one. Reverse-leg reverse-referral tracking, monthly
                CSV reconciliation, and member credit ledger all live in preview as of today.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
